import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Mail, Lock, AlertCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodI18nResolver } from '../lib/validation';
import { loginCredentialsSchema, type LoginCredentials } from '../schemas/auth';
import Auth3DLayout from '../components/auth3d/Auth3DLayout';
import GlassPanel from '../components/auth3d/GlassPanel';
import AuthInput from '../components/auth3d/AuthInput';
import AuthButton from '../components/auth3d/AuthButton';

const LoginPage: React.FC = () => {
  const { login, isAuthenticated, isLoading, error } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    clearErrors: clearFormErrors,
  } = useForm<LoginCredentials>({
    resolver: zodI18nResolver(loginCredentialsSchema),
  });

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to='/' replace />;
  }

  const onSubmit = async (data: LoginCredentials) => {
    clearFormErrors(['email', 'password']);

    try {
      const result = await login(data.email, data.password);

      if (!result.success) {
        // Error is set by AuthContext
      } else {
        // Redirect to home page after successful login
        navigate('/', { replace: true });
      }
    } catch (err) {
      // Error is set by AuthContext
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Auth3DLayout>
      <GlassPanel delay={0.5}>
        <div className='p-8 sm:p-10 space-y-8'>
          {/* Title */}
          <div className='text-center space-y-4'>
            <div className='space-y-2'>
              <h1
                className='text-3xl sm:text-4xl font-bold text-white'
                style={{ fontFamily: 'Orbitron, sans-serif' }}
              >
                {t('auth.signIn')}
              </h1>
              <p className='text-gray-400 text-sm'>
                {t('auth.dontHaveAccount')}{' '}
                <Link
                  to='/register'
                  className='text-cyan-400 hover:text-cyan-300 transition-colors font-medium'
                >
                  {t('auth.signUp')}
                </Link>
              </p>
            </div>
          </div>

          {/* Form */}
          <form
            className='space-y-5'
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            data-testid='login-form'
          >
            {/* Email Field */}
            <AuthInput
              id='email'
              label={t('auth.email')}
              type='email'
              placeholder={t('auth.enterEmail')}
              icon={Mail}
              error={errors.email?.message}
              {...register('email')}
              autoComplete='email'
              data-testid='login-email-input'
            />

            {/* Password Field */}
            <div className='relative'>
              <AuthInput
                id='password'
                label={t('auth.password')}
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.enterPassword')}
                icon={Lock}
                error={errors.password?.message}
                {...register('password')}
                autoComplete='current-password'
                data-testid='login-password-input'
                className='pr-12'
              />
              <button
                type='button'
                className='absolute right-3 top-[2.6rem] text-gray-400 hover:text-cyan-400 transition-colors'
                onClick={togglePasswordVisibility}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* Error Display */}
            {error && (
              <div className='rounded-lg bg-red-500/10 border border-red-500/30 p-4 backdrop-blur-sm'>
                <div className='flex gap-3'>
                  <AlertCircle className='h-5 w-5 text-red-400 flex-shrink-0 mt-0.5' />
                  <p className='text-sm text-red-300'>{error}</p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <AuthButton
              type='submit'
              isLoading={isSubmitting || isLoading}
              loadingText={t('common.loading')}
              data-testid='login-submit-button'
              className='mt-6'
            >
              {t('auth.login')}
            </AuthButton>
          </form>
        </div>
      </GlassPanel>
    </Auth3DLayout>
  );
};

export default LoginPage;
